/* ============================================================
   Shop the Feed — autoplay-in-view (videos always muted).
   - Plays each card's video only while it's in the viewport
     (saves battery/bandwidth, especially on mobile).
   - Videos are always muted (no sound control) so autoplay is
     allowed by browsers.
   Self-contained, no dependencies. Safe to load once per page.
   ============================================================ */
(function () {
  function initFeed(root) {
    var videos = root.querySelectorAll('.stf__video');
    if (!videos.length) return;

    // Fade the poster out the moment the video is actually rendering frames,
    // so the card never shows a blank box mid-scroll. Each video is a facade:
    // its <source> children are read once, the best URL for this browser is
    // stashed on the element, and the sources are removed — so an off-screen
    // card is just a poster <img>, not a live media player. Ten mounted
    // players (three of them 1080p mp4) is what OOM-killed the page in the
    // Instagram in-app webview, Android's Chrome WebView worst of all.
    videos.forEach(function (v) {
      if (v._stfInit) return; // shopify:section:load re-runs initFeed
      v._stfInit = true;
      var media = v.closest('.stf__media');
      var reveal = function () { if (media) media.classList.add('is-playing'); };
      v.addEventListener('playing', reveal);
      // Some browsers fire only 'timeupdate' once decoding starts.
      v.addEventListener('timeupdate', function () { if (v.currentTime > 0) reveal(); });

      // Prefer HLS where it plays natively (iOS/Safari — adaptive, so the
      // browser can downshift under memory pressure); everywhere else take
      // the first non-HLS source (Chrome ignores m3u8 in <video>).
      var chosen = '';
      var sources = Array.from(v.querySelectorAll('source'));
      sources.forEach(function (s) {
        var type = s.getAttribute('type') || '';
        var isHls = /mpegurl/i.test(type);
        if (!chosen && !isHls) chosen = s.getAttribute('src') || '';
        if (isHls && v.canPlayType('application/vnd.apple.mpegurl')) chosen = s.getAttribute('src') || '';
        s.remove();
      });
      v.dataset.stfSrc = chosen;
    });

    function attach(v) {
      if (!v.dataset.stfSrc) return;
      if (!v.getAttribute('src')) { v.src = v.dataset.stfSrc; v.load(); }
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
    function detach(v) {
      v.pause();
      // Dropping src + load() is the only reliable way to make the browser
      // release the decoder and buffered data; pause() alone keeps it all.
      if (v.getAttribute('src')) { v.removeAttribute('src'); v.load(); }
      var media = v.closest('.stf__media');
      if (media) media.classList.remove('is-playing'); // poster back over the card
    }

    // Play when visible, tear down when not. `rootMargin` starts buffering
    // before the card is centered so playback is ready by the time it's on
    // screen. Falls back to "attach and play" if IntersectionObserver is
    // unavailable.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) attach(entry.target);
          else detach(entry.target);
        });
      }, { threshold: 0.1, rootMargin: '0px 300px 0px 300px' });
      videos.forEach(function (v) { io.observe(v); });
    } else {
      videos.forEach(attach);
    }

    // Videos are always muted (no sound control) — the <video> keeps `muted`.

    // Shop the Look: open each card's drawer from its pill.
    root.querySelectorAll('.stf__look[data-stf-drawer]').forEach(function (btn) {
      // Resolve the drawer once, now — after the first open it gets portaled
      // to <body>, so re-querying the card would return null on later clicks.
      var card = btn.closest('.stf__card');
      var drawer = card && card.querySelector('.stf__drawer');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (drawer) openDrawer(drawer);
      });
    });
    root.querySelectorAll('.stf__drawer').forEach(function (drawer) {
      // Close on backdrop click (but not when clicking inside the panel).
      drawer.addEventListener('click', function (e) {
        if (e.target === drawer) closeDrawer(drawer);
      });
      var closeBtn = drawer.querySelector('.stf__drawer-close');
      if (closeBtn) closeBtn.addEventListener('click', function () { closeDrawer(drawer); });
    });

    // Tapping a product row in the drawer opens the native quick-add modal
    // instead of navigating to the product page (mobile AND desktop). Each row
    // is wrapped in a <product-card>, which registers its OWN click->navigate
    // handler in the bubble phase; we listen in the CAPTURE phase so ours runs
    // first, and call stopImmediatePropagation to suppress that navigation.
    // Same trigger for the on-card tag thumbnails (the small product chips
    // overlaid bottom-left on each video card) and the drawer rows. Both are
    // <product-card>s with a hidden <quick-add-component>; intercept in the
    // CAPTURE phase to beat the card's own navigate-to-PDP handler.
    root.querySelectorAll('.stf__drawer-card, .stf__tag-card').forEach(function (card) {
      if (card._stfQaBound) return; // guard against re-init double-binding
      card._stfQaBound = true;
      card.addEventListener(
        'click',
        function (e) {
          var qa = card.querySelector('quick-add-component');
          if (!qa || typeof qa.handleClick !== 'function') return; // sold out / not upgraded yet
          e.preventDefault();
          e.stopImmediatePropagation();
          qa.handleClick(new MouseEvent('click', { bubbles: true, cancelable: true }));
        },
        true
      );
    });
  }

  function openDrawer(d) {
    // The drawer markup lives inside .stf__card, which is `overflow:hidden`
    // and sits inside the horizontally-scrolling .stf__track. That traps the
    // drawer's stacking context and clips it, so on mobile the bottom sheet
    // got hidden behind later cards/sections. Portal it to <body> on first
    // open so its `position:fixed; inset:0; z-index:1000` is relative to the
    // viewport with nothing above it. (Idempotent — no-op once moved.)
    if (d.parentElement !== document.body) document.body.appendChild(d);
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.classList.add('stf-drawer-open');
  }
  function closeDrawer(d) {
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('stf-drawer-open');
  }
  // ESC closes whichever drawer is open.
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      var open = document.querySelector('.stf__drawer.is-open');
      if (open) closeDrawer(open);
    }
  });

  function boot() {
    document.querySelectorAll('.stf').forEach(initFeed);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when a merchant adds/edits the section in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('.stf') : null;
    if (el) initFeed(el);
  });
})();

